import Check from '@mui/icons-material/Check';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { Button } from '@src/common/components/button/Button';
import { Divider } from '@src/common/components/Divider';
import { Stack } from '@src/common/components/Stack';
import { ToastLevel } from '@src/common/components/ToastLevel';
import { useToast } from '@src/common/hooks/useToast';
import { theme } from '@src/common/styles/Theme';
import { CloseEditOrderButton } from '@src/orders/components/CloseEditOrderButton';
import type { Order } from '@src/orders/domain/Order';
import { OrderStatus } from '@src/orders/domain/Order';
import { useNavigateToOrderDetails } from '@src/orders/hooks/useNavigateToOrderDetails';
import { useRefreshOrder } from '@src/orders/hooks/useRefreshOrder';
import { useUpdateOrder } from '@src/orders/hooks/useUpdateOrder';
import { useUpdateOrderAsDraft } from '@src/orders/hooks/useUpdateOrderAsDraft';
import { useTranslation } from 'react-i18next';

export function AdminEditOrderFooter({
  order,
  readFormValues,
  formIsDirty,
}: {
  order: Order;
  formIsDirty: boolean;
  readFormValues: () => Record<string, unknown>;
}) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { refreshOrder } = useRefreshOrder({ id: order.id });
  const { navigateToOrderDetails } = useNavigateToOrderDetails();
  const fields = order.getFields().fields;

  const { updateOrderAsDraft, isPending: isPendingUpdateOrderAsDraft } =
    useUpdateOrderAsDraft({
      orderId: order.id,
      onSuccess: () => {
        refreshOrder();
        showToast({
          severity: ToastLevel.success,
          message: t('editOrder.success'),
        });
        void navigateToOrderDetails({ orderId: order.id });
      },
      onError: () =>
        showToast({
          severity: ToastLevel.error,
          message: t('editOrder.error'),
        }),
    });

  const saveProgress = () => {
    updateOrderAsDraft({ data: readFormValues(), fields });
  };

  const { updateOrder, isPending: isPendingUpdateOrder } = useUpdateOrder({
    orderId: order.id,
    onSuccess: () => {
      refreshOrder();
      void navigateToOrderDetails({ orderId: order.id });
    },
    onError: () =>
      showToast({
        severity: ToastLevel.error,
        message: t('editOrder.error'),
      }),
  });

  const isDraft = order.status === OrderStatus.DRAFT;
  const onSave = () => {
    if (isDraft) {
      saveProgress();
    } else {
      updateOrder({
        data: readFormValues(),
        fields,
      });
    }
  };

  const disabled = isPendingUpdateOrder || isPendingUpdateOrderAsDraft;
  return (
    <Stack
      zIndex={10}
      position="sticky"
      bottom="0"
      style={{ backgroundColor: theme.palette.surface.surface }}
      paddingBottom={theme.spacing['6']}
      marginX="40px"
    >
      <Divider style={{ marginBottom: theme.spacing['6'] }} />
      <Stack direction="row" justifyContent="space-between">
        <CloseEditOrderButton
          orderId={order.id}
          formIsDirty={formIsDirty}
          onSave={onSave}
        />
        <Stack direction="row" gap={theme.spacing['2']}>
          {isDraft && (
            <Button
              type="button"
              disabled={disabled}
              onClick={saveProgress}
              variant="outlined"
              startIcon={<SaveOutlinedIcon />}
            >
              {t('saveProgress')}
            </Button>
          )}
          <Button
            type="submit"
            disabled={disabled}
            startIcon={<Check />}
            variant="contained"
          >
            {t('submit')}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
